import React, { type FocusEvent } from 'react';
import { classNames } from 'tradera/utils/class-names';
import styles from './base-input-field.module.scss';
import { composeEventHandlers } from 'tradera/utils/compose-event-handler';

export type BaseInputFieldProps = {
    suffix?: React.ReactNode;
    isInvalid?: boolean;
    align?: 'left' | 'center' | 'right';
    hidePlaceholderOnFocus?: boolean;
    noLigatures?: boolean;
    selectAllOnFocus?: boolean;
    preventBubbling?: boolean;
} & React.ComponentPropsWithRef<'input'>;

/**
 * This component is used as a base for other input fields.
 * It's not meant to be used directly.
 *
 */
export const BaseInputField = React.forwardRef<
    HTMLInputElement,
    BaseInputFieldProps
>(function BaseInputField(
    {
        isInvalid,
        className,
        style,
        suffix,
        name,
        hidePlaceholderOnFocus,
        noLigatures,
        selectAllOnFocus,
        preventBubbling,
        onClick,
        ...props
    },
    ref,
) {
    const onFocus = selectAllOnFocus
        ? composeEventHandlers(
              props.onFocus,
              (e: FocusEvent<HTMLInputElement>) => e.target.select(),
          )
        : props.onFocus;

    return (
        <div
            className={classNames(
                styles.inputWrapper,
                {
                    [styles.error]: isInvalid,
                },
                className,
            )}
            style={style}>
            <input
                id={name}
                name={name}
                className={classNames(styles.input, {
                    'text-right': props.align === 'right',
                    'text-center': props.align === 'center',
                    [styles.hidePlaceholderOnFocus]: hidePlaceholderOnFocus,
                    [styles.noLigatures]: noLigatures,
                })}
                aria-invalid={isInvalid}
                ref={ref}
                onClick={(e) => {
                    if (preventBubbling) {
                        e.stopPropagation();
                    }
                    onClick?.(e);
                }}
                {...props}
                onFocus={onFocus}
            />
            {typeof suffix === 'undefined' ? null : (
                <div
                    className={classNames(
                        props.disabled && styles.disabled,
                        // this py-1-1 takes the border of the inputWrapper into account so that the
                        // component follows the rhythm spacing. Without the offset the input
                        // will be <border-width> too high.
                        'mr-1 pt-1 pb-1-1',
                    )}>
                    {suffix}
                </div>
            )}
        </div>
    );
});
